import axios, { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import {
  AdminUser,
  SystemStats,
  ServerStatus,
  SystemLog,
  StoreManagement,
  BackupInfo,
  ApiUsageStats,
  SecurityEvent,
  AdminDashboardData,
  QuickCounts,
  SubscriptionSummary,
  ApiMonitoringStats,
  ApiCallLog,
  ApiErrorPattern,
  ApiMonitoringAlert,
  SystemSettings,
  SystemSettingsResponse,
  AnalyticsData,
  DiagnosticReport,
  DiagnosticSummary,
  BroadcastNotificationHistory,
  SendBroadcastNotificationRequest,
  SendBroadcastNotificationResponse,
  BroadcastNotificationHistoryListResponse,
  ScheduledBroadcastNotification,
  ScheduledBroadcastNotificationListResponse,
  CreateScheduledBroadcastNotificationRequest,
  UpdateScheduledBroadcastNotificationRequest,
  Banner,
  CreateBannerRequest,
  UpdateBannerRequest,
  BannerClickRequest,
  BannerStats,
  BannerClickLog,
  PagedResponse,
  ImageUploadResponse,
  UpdatePriorityRequest,
  SystemHealthSummary,
  ServiceHealth,
  PromotionCodeAdmin,
  CreatePromotionRequest,
  UpdatePromotionRequest,
  PartnerListDto,
  PartnerDetailDto,
  PartnerSettlementsDto,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  Partner,
} from '../types/admin';

import { getSafeApiUrl } from '../config/environment';

// 관리자 토큰 키 상수
const ADMIN_TOKEN_KEY = 'adminToken';
const ADMIN_USER_KEY = 'currentAdmin'; // 기존 코드와 호환성 유지

// 로컬 NotificationSettings 타입 정의
interface NotificationSettings {
  enableEmailNotifications: boolean;
  enableWebSocketNotifications: boolean;
  enableBrowserNotifications: boolean;
  minPriority: 'Low' | 'Normal' | 'High' | 'Critical';
  notificationTypes: {
    userRegistration: boolean;
    orderFailure: boolean;
    securityEvent: boolean;
    systemAlert: boolean;
  };
}

// API 기본 URL 가져오기
const API_BASE_URL = getSafeApiUrl('GATEWAY');

console.log('🔄 [NEW ADMIN API] 초기화 시작');
console.log('🔄 [NEW ADMIN API] API_BASE_URL:', API_BASE_URL);

// 간단한 관리자 전용 API 인스턴스 (인터셉터 없음)
const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
      timeout: 30000, // 30초로 증가
});

console.log('🔄 [NEW ADMIN API] adminApi.defaults.baseURL:', adminApi.defaults.baseURL);

// 토큰과 함께 헤더를 생성하는 헬퍼 함수
const createAuthHeaders = () => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  const headers: any = {
    'Content-Type': 'application/json',
    'X-Direct-Header-Set': 'true',
    'X-Request-Time': new Date().toISOString(),
    'X-Request-Source': 'NEW-ADMIN-API',
  };
  
  console.log('🔧 [AUTH HEADERS] 헤더 생성 중...');
  console.log('🔑 [AUTH HEADERS] 토큰 존재:', !!token);
  console.log('💾 [AUTH HEADERS] localStorage 키:', Object.keys(localStorage));
  
  if (token && token.length > 0) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['X-Token-Found'] = 'true';
    headers['X-Token-Length'] = token.length.toString();
    headers['X-Token-Preview'] = token.substring(0, 20) + '...';
    console.log('✅ [AUTH HEADERS] Authorization 헤더 추가됨:', `Bearer ${token.substring(0, 20)}...`);
  } else {
    headers['X-Token-Found'] = 'false';
    headers['X-Token-Error'] = 'No token in localStorage';
    console.log('❌ [AUTH HEADERS] 토큰 없음');
  }
  
  console.log('📋 [AUTH HEADERS] 최종 헤더:', Object.keys(headers));
  
  return headers;
};

// 안전한 API 호출 래퍼
export const safeApiCall = async <T>(method: 'get' | 'post' | 'put' | 'delete', url: string, data?: any): Promise<T> => {
  console.log(`🚀 [SAFE API CALL] ${method.toUpperCase()} ${url} 시작`);
  
  try {
    const headers = createAuthHeaders();
    
    let response: AxiosResponse<T>;
    
    if (method === 'get') {
      response = await adminApi.get<T>(url, { headers });
    } else if (method === 'post') {
      response = await adminApi.post<T>(url, data, { headers });
    } else if (method === 'put') {
      response = await adminApi.put<T>(url, data, { headers });
    } else if (method === 'delete') {
      response = await adminApi.delete<T>(url, { headers });
      } else {
      throw new Error(`지원하지 않는 HTTP 메서드: ${method}`);
    }
    
    console.log(`✅ [SAFE API CALL] ${method.toUpperCase()} ${url} 성공:`, response.status);
    return response.data;
  } catch (error: any) {
    console.error(`❌ [SAFE API CALL] ${method.toUpperCase()} ${url} 실패:`, error);
    // 관리자 PWA: 명시적 로그아웃이 아닌 한 로그인 유지. 401(만료 등) 시에도 토큰을 삭제하지 않음.
    // 토큰 삭제는 AdminAuthContext의 logout()에서만 수행.
    throw error;
  }
};

// 토큰 관리 함수들
export const setAuthHeader = (token: string) => {
  console.log('🔧 [SET AUTH HEADER] 토큰 설정:', token ? `${token.substring(0, 20)}...` : 'null');
  // 더 이상 기본 헤더에 설정하지 않음 - 각 요청마다 직접 설정
};

export const saveAdminToken = (token: string) => {
  console.log('💾 [SAVE TOKEN] 토큰 저장:', token ? `${token.substring(0, 20)}...` : 'null');
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
};

export const loadAdminToken = (): string | null => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  console.log('📖 [LOAD TOKEN] 토큰 로드:', token ? `${token.substring(0, 20)}...` : 'null');
      return token;
};

// 관리자 인증 API
export const adminAuth = {
  login: async (username: string, password: string): Promise<{ token: string; user: AdminUser }> => {
    console.log('🔐 [ADMIN LOGIN] 로그인 시도:', username);
    
    try {
      const response = await adminApi.post<{ token: string; user: AdminUser }>('/auth/login', {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Source': 'ADMIN-LOGIN',
          'X-Request-Time': new Date().toISOString(),
        }
      });
      
      console.log('✅ [ADMIN LOGIN] 로그인 성공');
      return response.data;
    } catch (error) {
      console.error('❌ [ADMIN LOGIN] 로그인 실패:', error);
      throw error;
    }
  },
  
  getProfile: async (): Promise<AdminUser> => {
    console.log('👤 [ADMIN PROFILE] 프로필 조회');
    return safeApiCall<AdminUser>('get', '/profile');
  },

  updateProfile: async (profile: Partial<AdminUser>): Promise<{ success: boolean; message: string; user: AdminUser }> => {
    console.log('👤 [ADMIN PROFILE] 프로필 업데이트:', profile);
    return safeApiCall<{ success: boolean; message: string; user: AdminUser }>('put', '/profile', profile);
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    console.log('🔐 [ADMIN PROFILE] 비밀번호 변경');
    return safeApiCall<{ success: boolean; message: string }>('put', '/profile/password', {
      currentPassword,
      newPassword
    });
  },

  logout: async (): Promise<void> => {
    console.log('🚪 [ADMIN LOGOUT] 로그아웃');
    try {
      await safeApiCall<void>('post', '/auth/logout');
    } catch (error) {
      console.warn('⚠️ [ADMIN LOGOUT] 로그아웃 요청 실패 (무시):', error);
    } finally {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_USER_KEY);
    }
  }
};

// 대시보드 데이터
export const adminDashboard = {
  getDashboardData: async (startDate?: string, endDate?: string): Promise<AdminDashboardData> => {
    console.log('🎯 [DASHBOARD] 대시보드 데이터 요청', { startDate, endDate });
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString();
    const url = queryString ? `/dashboard?${queryString}` : '/dashboard';
    return safeApiCall<AdminDashboardData>('get', url);
  },
  
  getSystemStats: async (startDate?: string, endDate?: string): Promise<SystemStats> => {
    console.log('📊 [DASHBOARD] 시스템 통계 요청', { startDate, endDate });
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString();
    const url = queryString ? `/dashboard/stats?${queryString}` : '/dashboard/stats';
    return safeApiCall<SystemStats>('get', url);
  },
  
  getMonthlyStats: async (params?: { startYear?: number; endYear?: number }): Promise<any[]> => {
    const search = new URLSearchParams();
    if (params?.startYear != null) search.append('startYear', String(params.startYear));
    if (params?.endYear != null) search.append('endYear', String(params.endYear));
    const qs = search.toString();
    const url = qs ? `/dashboard/monthly-stats?${qs}` : '/dashboard/monthly-stats';
    console.log('📅 [DASHBOARD] 월별 통계 요청', params);
    return safeApiCall<any[]>('get', url);
  },
  
  /** 서버 상태: system-health API 기반 실제 헬스 체크 결과를 ServerStatus 형식으로 반환 */
  getServerStatus: async (): Promise<ServerStatus[]> => {
    console.log('🔧 [DASHBOARD] 서버 상태 요청 (system-health 기반)');
    try {
      const summary = await safeApiCall<SystemHealthSummary>('get', '/system-health');
      const raw = Array.isArray(summary?.services) ? summary.services : [];
      const services = raw.filter((s): s is ServiceHealth => s != null && typeof s === 'object');
      return services.map((s: ServiceHealth): ServerStatus => ({
        serviceName: s.serviceName ?? '',
        status: s.status === 'healthy' || s.status === 'degraded' ? 'running' : s.status === 'down' ? 'error' : 'stopped',
        uptime: s.uptime ?? 0,
        memoryUsage: s.memoryUsage ?? undefined,
        cpuUsage: s.cpuUsage ?? undefined,
        lastCheck: s.lastCheck ?? new Date().toISOString(),
      }));
    } catch (e) {
      console.warn('[DASHBOARD] system-health 조회 실패, 빈 서버 상태 반환:', e);
      return [];
    }
  },

  getSystemLogs: async (level?: string, limit?: number): Promise<SystemLog[]> => {
    console.log('📋 [DASHBOARD] 시스템 로그 요청');
    const params = new URLSearchParams();
    if (level) params.append('level', level);
    if (limit) {
      params.append('pageSize', limit.toString());
      params.append('page', '1');
    }
    
    const queryString = params.toString();
    const url = queryString ? `/system-logs?${queryString}` : '/system-logs';
    
    const response = await safeApiCall<{ items: SystemLog[], total: number, page: number, pageSize: number }>('get', url);
    return response.items || [];
  },

  getSecurityEvents: async (limit?: number): Promise<SecurityEvent[]> => {
    console.log('🔒 [DASHBOARD] 보안 이벤트 요청');
    const url = limit ? `/dashboard/security-events?limit=${limit}` : '/dashboard/security-events';
    const data = await safeApiCall<SecurityEvent[]>('get', url);
    return Array.isArray(data) ? data : [];
  },
  /** 보안 이벤트 해결 처리 (미해결 → 해결됨) */
  resolveSecurityEvent: async (eventId: string): Promise<{ success: boolean; message?: string }> => {
    const res = await safeApiCall<{ success: boolean; message?: string }>('post', `/dashboard/security-events/${eventId}/resolve`);
    return res ?? { success: false, message: '응답 없음' };
  },
  /** 보안 이벤트 선택 삭제 (복수 ID) */
  deleteSecurityEvents: async (eventIds: string[]): Promise<{ success: boolean; message?: string }> => {
    const ids = Array.isArray(eventIds) ? eventIds.filter(Boolean) : [];
    if (ids.length === 0) return { success: false, message: '삭제할 항목이 없습니다.' };
    const res = await safeApiCall<{ success: boolean; message?: string }>('post', '/dashboard/security-events/bulk-delete', { eventIds: ids });
    return res ?? { success: false, message: '응답 없음' };
  },
  /** 보안 이벤트 전체 삭제 */
  deleteAllSecurityEvents: async (): Promise<{ success: boolean; message?: string }> => {
    const res = await safeApiCall<{ success: boolean; message?: string }>('delete', '/dashboard/security-events/all');
    return res ?? { success: false, message: '응답 없음' };
  },

  /** 퀵 액션/배지용 4종 건수 (기존 API 조합) */
  getQuickCounts: async (): Promise<QuickCounts> => {
    const [pendingRes, paymentList, failedSummary, suspendedList] = await Promise.all([
      adminUserApprovals.getPendingUsers().catch(() => []),
      safeApiCall<any[]>('get', '/payment-failure-history').catch(() => []),
      adminFailedOrders.getFailedOrderSummary().catch(() => ({ totalFailed: 0, pendingRetry: 0 })),
      safeApiCall<any[]>('get', '/suspended-accounts').catch(() => []),
    ]);
    const pendingCount = Array.isArray(pendingRes) ? pendingRes.length : 0;
    const paymentCount = Array.isArray(paymentList) ? paymentList.length : 0;
    const failedCount = failedSummary?.totalFailed ?? failedSummary?.pendingRetry ?? 0;
    const suspendedCount = Array.isArray(suspendedList) ? suspendedList.length : 0;
    return {
      pendingApprovals: pendingCount,
      paymentFailures: paymentCount,
      failedOrders: failedCount,
      suspendedAccounts: suspendedCount,
    };
  },

  /** 메인 대시보드 KPI용 구독 요약 (기존 statistics 호출) */
  getSubscriptionSummary: async (): Promise<SubscriptionSummary | null> => {
    const data = await safeApiCall<any>('get', '/subscription-dashboard/statistics');
    if (!data) return null;
    return {
      mrr: data.mrr ?? 0,
      arr: data.arr ?? 0,
      activeSubscriptions: data.activeSubscriptions ?? 0,
      mrrGrowthRate: data.mrrGrowthRate ?? 0,
    };
  },
};

// 사용자 관리
export const adminUsers = {
  getUsers: async (params?: any): Promise<{ items: AdminUser[], total: number, page: number, pageSize: number }> => {
    console.log('👥 [USERS] 사용자 목록 조회:', params);
    // params가 있으면 쿼리 스트링으로 변환
    let url = '/users';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key].toString());
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    // 백엔드는 { items, total, page, pageSize } 형태로 응답
    const response = await safeApiCall<{ items: AdminUser[], total: number, page: number, pageSize: number }>('get', url);
    return response;
  },

  createUser: async (userData: Partial<AdminUser>): Promise<AdminUser> => {
    console.log('➕ [USERS] 사용자 생성:', userData);
    return safeApiCall<AdminUser>('post', '/users', userData);
  },

  updateUser: async (userId: number | string, userData: Partial<AdminUser>): Promise<AdminUser> => {
    console.log('✏️ [USERS] 사용자 업데이트:', userId);
    return safeApiCall<AdminUser>('put', `/users/${userId}`, userData);
  },

  deleteUser: async (userId: number | string): Promise<void> => {
    console.log('🗑️ [USERS] 사용자 삭제:', userId);
    return safeApiCall<void>('delete', `/users/${userId}`);
  },

  toggleUserStatus: async (userId: number | string): Promise<void> => {
    console.log('🔄 [USERS] 사용자 상태 토글:', userId);
    return safeApiCall<void>('post', `/users/${userId}/toggle-status`);
  },

  suspendUser: async (userId: number | string): Promise<void> => {
    console.log('⏸️ [USERS] 사용자 정지:', userId);
    return safeApiCall<void>('post', `/users/${userId}/suspend`);
  },

  activateUser: async (userId: number | string): Promise<void> => {
    console.log('▶️ [USERS] 사용자 활성화:', userId);
    return safeApiCall<void>('post', `/users/${userId}/activate`);
  },

  // 관리자 임시 로그인 토큰 생성
  generateImpersonateToken: async (storeId: number | string): Promise<{ token: string; user: any }> => {
    console.log('🔑 [IMPERSONATE] 임시 로그인 토큰 생성 요청:', storeId);
    return safeApiCall<{ token: string; user: any }>('post', `/users/${storeId}/impersonate`);
  }
};

// 매장 관리
export const adminStores = {
  getStores: async (params?: any): Promise<{ items: StoreManagement[], total: number, page: number, pageSize: number }> => {
    console.log('🏪 [STORES] 매장 목록 조회:', params);
    // params가 있으면 쿼리 스트링으로 변환
    let url = '/stores';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key].toString());
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    // 백엔드는 { items, total, page, pageSize } 형태로 응답
    const response = await safeApiCall<{ items: StoreManagement[], total: number, page: number, pageSize: number }>('get', url);
    return response;
  },

  updateStore: async (storeId: number | string, storeData: Partial<StoreManagement>): Promise<StoreManagement> => {
    console.log('✏️ [STORES] 매장 업데이트:', storeId);
    return safeApiCall<StoreManagement>('put', `/stores/${storeId}`, storeData);
  },

  updateStoreStatus: async (storeId: number | string, status: string): Promise<void> => {
    console.log('🔄 [STORES] 매장 상태 업데이트:', storeId, status);
    return safeApiCall<void>('post', `/stores/${storeId}/status`, { status });
  },

  getStoreAnalytics: async (storeId: number | string): Promise<any> => {
    console.log('📊 [STORES] 매장 분석 데이터 조회:', storeId);
    return safeApiCall<any>('get', `/stores/${storeId}/analytics`);
  },

  approveStore: async (storeId: number | string): Promise<void> => {
    console.log('✅ [STORES] 매장 승인:', storeId);
    return safeApiCall<void>('post', `/stores/${storeId}/approve`);
  },

  suspendStore: async (storeId: number | string, reason: string): Promise<void> => {
    console.log('⏸️ [STORES] 매장 정지:', storeId);
    return safeApiCall<void>('post', `/stores/${storeId}/suspend`, { reason });
  },

  forceLogout: async (storeId: number | string): Promise<void> => {
    console.log('🚪 [STORES] 강제 로그아웃:', storeId);
    return safeApiCall<void>('post', `/stores/${storeId}/force-logout`);
  },

  getStoreStatistics: async (storeId: number | string, startDate?: string, endDate?: string): Promise<import('../types/admin').StoreStatistics> => {
    console.log('📊 [STORES] 매장 통계 조회:', storeId, { startDate, endDate });
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString();
    const url = queryString ? `/stores/${storeId}/statistics?${queryString}` : `/stores/${storeId}/statistics`;
    return safeApiCall<import('../types/admin').StoreStatistics>('get', url);
  },

  /** 상단 요약 카드용 통계 (전체 매장 기준). startDate/endDate 없으면 전체 기간 */
  getStoresStatisticsSummary: async (params?: { startDate?: string; endDate?: string }): Promise<{
    totalStores: number;
    activeStores: number;
    totalOrders: number;
    totalRevenue: number;
    totalCost: number;
    totalProfit: number;
  }> => {
    let url = '/stores/statistics/summary';
    if (params?.startDate != null && params?.endDate != null) {
      const q = new URLSearchParams();
      q.set('startDate', params.startDate);
      q.set('endDate', params.endDate);
      url += `?${q.toString()}`;
    }
    const res = await safeApiCall<{
      totalStores?: number;
      activeStores?: number;
      totalOrders?: number;
      totalRevenue?: number;
      totalCost?: number;
      totalProfit?: number;
    }>('get', url);
    return {
      totalStores: res?.totalStores ?? 0,
      activeStores: res?.activeStores ?? 0,
      totalOrders: res?.totalOrders ?? 0,
      totalRevenue: res?.totalRevenue ?? 0,
      totalCost: res?.totalCost ?? 0,
      totalProfit: res?.totalProfit ?? 0,
    };
  },

  // 원시 데이터 수집 설정 업데이트
  updateStoreRawDataCollection: async (storeId: number | string, collectRawOrderData: boolean): Promise<{ success: boolean; message: string; storeId: number; collectRawOrderData: boolean }> => {
    console.log('📝 [STORES] 원시 데이터 수집 설정 업데이트:', storeId, collectRawOrderData);
    return safeApiCall<{ success: boolean; message: string; storeId: number; collectRawOrderData: boolean }>('put', `/stores/${storeId}/raw-data-collection`, { collectRawOrderData });
  },

  // 원시 데이터가 있는 주문 목록 조회
  getStoreRawOrders: async (storeId: number | string): Promise<Array<{ id: number; orderNumber: string; sourcePlatform: string; orderTime: string; totalAmount: number; status: string; hasRawData: boolean }>> => {
    console.log('📋 [STORES] 원시 데이터 주문 목록 조회:', storeId);
    return safeApiCall<Array<{ id: number; orderNumber: string; sourcePlatform: string; orderTime: string; totalAmount: number; status: string; hasRawData: boolean }>>('get', `/stores/${storeId}/raw-orders`);
  },

  // 원시 데이터 상세 조회
  getOrderRawData: async (orderId: number | string): Promise<{ orderId: number; orderNumber: string; sourcePlatform: string; orderTime: string; rawData: string }> => {
    console.log('📄 [STORES] 원시 데이터 상세 조회:', orderId);
    return safeApiCall<{ orderId: number; orderNumber: string; sourcePlatform: string; orderTime: string; rawData: string }>('get', `/orders/${orderId}/raw-data`);
  }
};

// 실패한 주문 관리
export const adminFailedOrders = {
  getFailedOrders: async (): Promise<any[]> => {
    console.log('📋 [FAILED ORDERS] 실패한 주문 조회');
    return safeApiCall<any[]>('get', '/failed-orders');
  },

  getFailedOrderSummary: async (): Promise<any> => {
    console.log('📊 [FAILED ORDERS] 실패한 주문 요약');
    return safeApiCall<any>('get', '/failed-orders/summary');
  },

  getFailedOrderById: async (orderId: string | number): Promise<any> => {
    console.log('📄 [FAILED ORDERS] 실패한 주문 상세:', orderId);
    return safeApiCall<any>('get', `/failed-orders/${orderId}`);
  },

  retryFailedOrder: async (orderId: string | number, forceRetry?: boolean) => {
    console.log('🔄 [FAILED ORDERS] 실패한 주문 재시도:', orderId, forceRetry);
    return safeApiCall<import('../types/admin').RetryFailedOrderResponse>('post', `/failed-orders/${orderId}/retry`, { forceRetry });
  },

  updateFailedOrderStatus: async (orderId: string | number, status: string): Promise<void> => {
    console.log('🔄 [FAILED ORDERS] 실패한 주문 상태 업데이트:', orderId, status);
    return safeApiCall<void>('put', `/failed-orders/${orderId}/status`, { status });
  },

  retryOrder: async (orderId: string): Promise<void> => {
    console.log('🔄 [FAILED ORDERS] 주문 재시도 (구 메서드):', orderId);
    return safeApiCall<void>('post', `/failed-orders/${orderId}/retry`);
  },

  deleteFailedOrder: async (orderId: string): Promise<void> => {
    console.log('🗑️ [FAILED ORDERS] 실패한 주문 삭제:', orderId);
    return safeApiCall<void>('delete', `/failed-orders/${orderId}`);
  }
};

// API 모니터링
export const adminApiMonitoring = {
  getStats: async (): Promise<ApiMonitoringStats> => {
    console.log('📊 [API MONITORING] 통계 조회');
    return safeApiCall<ApiMonitoringStats>('get', '/monitoring/stats');
  },

  getLogs: async (limit?: number): Promise<ApiCallLog[]> => {
    console.log('📋 [API MONITORING] 로그 조회');
    const url = limit ? `/monitoring/logs?limit=${limit}` : '/monitoring/logs';
    return safeApiCall<ApiCallLog[]>('get', url);
  },

  getErrorPatterns: async (): Promise<ApiErrorPattern[]> => {
    console.log('🔍 [API MONITORING] 오류 패턴 조회');
    return safeApiCall<ApiErrorPattern[]>('get', '/monitoring/error-patterns');
  },

  getAlerts: async (): Promise<ApiMonitoringAlert[]> => {
    console.log('🚨 [API MONITORING] 알림 조회');
    return safeApiCall<ApiMonitoringAlert[]>('get', '/monitoring/alerts');
  }
};

// 백업 관리
export const adminBackup = {
  getBackups: async (): Promise<BackupInfo[]> => {
    console.log('💾 [BACKUP] 백업 목록 조회');
    return safeApiCall<BackupInfo[]>('get', '/backup');
  },

  createBackup: async (): Promise<BackupInfo> => {
    console.log('💾 [BACKUP] 백업 생성');
    return safeApiCall<BackupInfo>('post', '/backup');
  },

  restoreBackup: async (backupId: string): Promise<void> => {
    console.log('🔄 [BACKUP] 백업 복원:', backupId);
    return safeApiCall<void>('post', `/backup/${backupId}/restore`);
  },

  deleteBackup: async (backupId: string): Promise<void> => {
    console.log('🗑️ [BACKUP] 백업 삭제:', backupId);
    return safeApiCall<void>('delete', `/backup/${backupId}`);
  }
};


// 디버깅 함수
export const debugAdminAuth = () => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  const user = localStorage.getItem(ADMIN_USER_KEY);
  
  const result = {
    token: token ? `${token.substring(0, 20)}...` : null,
    tokenLength: token?.length || 0,
    user: user ? JSON.parse(user) : null,
    localStorage: Object.keys(localStorage),
    timestamp: new Date().toISOString()
  };
  
  console.log('🔍 [DEBUG ADMIN AUTH] 현재 상태:', result);
  return result;
};

// 전역 디버깅 함수 등록
if (typeof window !== 'undefined') {
  (window as any).debugAdminAuth = debugAdminAuth;
  (window as any).getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
  (window as any).clearAdminTokens = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    console.log('🧹 [DEBUG] 토큰 및 정보 제거됨');
  };
}

console.log('✅ [NEW ADMIN API] 초기화 완료');

// 백워드 호환성을 위한 별칭 exports
export const apiMonitoring = {
  startRealtimeStream: async (): Promise<EventSource | null> => {
    console.log('🔄 [API MONITORING] 실시간 스트림 시작');
    try {
      // baseURL이 이미 /api를 포함하므로 /admin만 제거하면 됨
      // 예: http://localhost:5000/api/admin -> http://localhost:5000/api
      const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.warn('❌ [API MONITORING] 토큰이 없어 실시간 스트림을 시작할 수 없습니다.');
        return null;
      }
      
      // EventSource는 쿼리 파라미터로 토큰 전달 (Authorization 헤더 지원 안 함)
      // baseURL이 이미 /api를 포함하므로 /admin/monitoring/realtime만 추가
      const url = `${baseURL}/admin/monitoring/realtime?token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(url);
      
      return eventSource;
    } catch (error) {
      console.error('❌ [API MONITORING] 실시간 스트림 시작 실패:', error);
      return null;
    }
  },
  getMonitoringStats: adminApiMonitoring.getStats,
  getApiCallLogs: async (params: any): Promise<{ logs: ApiCallLog[], total: number }> => {
    console.log('📋 [API MONITORING] API 호출 로그 조회:', params);
    // params에서 필요한 정보 추출
    let url = '/monitoring/logs';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          // dateRange를 startDate와 endDate로 변환
          if (key === 'dateRange' && params[key]) {
            const dateRange = params[key];
            if (Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
              const startDate = dateRange[0].format('YYYY-MM-DD');
              const endDate = dateRange[1].format('YYYY-MM-DD');
              queryParams.append('startDate', startDate);
              queryParams.append('endDate', endDate);
            }
          } else {
            // 백엔드는 pageSize를 사용하므로 limit를 pageSize로 변환
            const paramKey = key === 'limit' ? 'pageSize' : key;
            queryParams.append(paramKey, params[key].toString());
          }
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    const response = await safeApiCall<{ logs: ApiCallLog[], total: number, page: number, pageSize: number } | ApiCallLog[]>('get', url);
    // 백엔드가 { logs, total, page, pageSize } 형태로 반환
    if (response && typeof response === 'object' && !Array.isArray(response) && 'logs' in response && 'total' in response) {
      return { logs: (response as { logs: ApiCallLog[], total: number }).logs, total: (response as { logs: ApiCallLog[], total: number }).total };
    }
    // 폴백: 배열로 반환되는 경우
    if (Array.isArray(response)) {
      return { logs: response, total: response.length };
    }
    return { logs: [], total: 0 };
  },
  getErrorPatterns: adminApiMonitoring.getErrorPatterns,
  getAlerts: adminApiMonitoring.getAlerts,
  getPathDuplicationStats: async () => {
    console.log('📊 [API MONITORING] 경로 중복 통계');
    return safeApiCall<any>('get', '/monitoring/path-duplications');
  },
  resolveAlert: async (alertId: string, username: string) => {
    console.log('✅ [API MONITORING] 알림 해결:', alertId, username);
    return safeApiCall<void>('put', `/monitoring/alerts/${alertId}/resolve`, { resolvedBy: username });
  }
}; // AdminApiMonitoring.tsx에서 사용

export const adminMonitoring = {
  getSystemLogs: async (params: any): Promise<{ items: SystemLog[], total: number }> => {
    console.log('📋 [ADMIN MONITORING] 시스템 로그 요청:', params);
    // params에서 필요한 정보 추출
    let url = '/system-logs';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key].toString());
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    const response = await safeApiCall<{ items: SystemLog[], total: number, page: number, pageSize: number } | SystemLog[]>('get', url);
    // 백엔드가 { items, total, page, pageSize } 형태로 응답
    if (response && typeof response === 'object' && 'items' in response) {
      return { items: response.items || [], total: response.total || 0 };
    }
    // 배열로 응답하는 경우 (하위 호환성)
    if (Array.isArray(response)) {
      return { items: response as SystemLog[], total: response.length };
    }
    return { items: [], total: 0 };
  },
  getSecurityEvents: adminDashboard.getSecurityEvents
}; // AdminLogs.tsx에서 사용

export const adminUserApprovals = {
  getPendingUsers: async () => {
    console.log('👥 [USER APPROVALS] 대기 중인 사용자 조회');
    return safeApiCall<any[]>('get', '/pending-users');
  },
  getRejectedUsers: async () => {
    console.log('❌ [USER APPROVALS] 거절된 사용자 조회');
    return safeApiCall<any[]>('get', '/rejected-users');
  },
  getApprovedUsers: async () => {
    console.log('✅ [USER APPROVALS] 승인된 사용자 조회');
    return safeApiCall<any[]>('get', '/approved-users');
  },
  getPendingUsersSummary: async () => {
    console.log('📊 [USER APPROVALS] 대기 사용자 요약');
    return safeApiCall<any>('get', '/pending-users/summary');
  },
  approveUser: async (userId: number, adminUsername: string) => {
    console.log('✅ [USER APPROVALS] 사용자 승인:', userId, adminUsername);
    return safeApiCall<void>('post', `/users/${userId}/approve`, { action: 'approve', adminUsername });
  },
  rejectUser: async (userId: number, reason: string, adminUsername: string) => {
    console.log('❌ [USER APPROVALS] 사용자 거절:', userId, reason, adminUsername);
    return safeApiCall<void>('post', `/users/${userId}/approve`, { action: 'reject', reason, adminUsername });
  }
}; // AdminUserApprovals.tsx에서 사용

// 서버 점검 모드 관리
export const adminServerMaintenance = {
  getStatus: async (): Promise<{ isMaintenanceMode: boolean }> => {
    console.log('🔧 [SERVER MAINTENANCE] 점검 모드 상태 조회');
    try {
      // baseURL이 이미 /api를 포함하므로 /api를 추가하지 않음
      const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
      const response = await fetch(`${baseURL}/server-maintenance-status`);
      if (!response.ok) {
        throw new Error('서버 점검 모드 상태 조회 실패');
      }
      return await response.json();
    } catch (error) {
      console.error('❌ [SERVER MAINTENANCE] 점검 모드 상태 조회 실패:', error);
      throw error;
    }
  },

  setStatus: async (enabled: boolean): Promise<{ success: boolean; message: string; isMaintenanceMode: boolean }> => {
    console.log(`🔧 [SERVER MAINTENANCE] 점검 모드 ${enabled ? '활성화' : '비활성화'}`);
    return safeApiCall<{ success: boolean; message: string; isMaintenanceMode: boolean }>('put', '/server-maintenance', { enabled });
  }
};

// 시스템 헬스 모니터링
export const adminSystemHealth = {
  getHealthSummary: async (): Promise<import('../types/admin').SystemHealthSummary> => {
    console.log('🏥 [SYSTEM HEALTH] 헬스 요약 조회');
    return safeApiCall<import('../types/admin').SystemHealthSummary>('get', '/system-health');
  },

  checkServiceHealth: async (serviceName: string): Promise<import('../types/admin').ServiceHealth> => {
    console.log(`🏥 [SYSTEM HEALTH] 서비스 헬스 체크: ${serviceName}`);
    return safeApiCall<import('../types/admin').ServiceHealth>('get', `/system-health/service/${serviceName}`);
  },

  checkDatabaseHealth: async (databaseName: string): Promise<import('../types/admin').DatabaseHealth> => {
    console.log(`🏥 [SYSTEM HEALTH] 데이터베이스 헬스 체크: ${databaseName}`);
    return safeApiCall<import('../types/admin').DatabaseHealth>('get', `/system-health/database/${databaseName}`);
  }
};

// 데이터 무결성 검증
export const adminDataIntegrity = {
  runIntegrityCheck: async (): Promise<import('../types/admin').DataIntegrityReport> => {
    console.log('🔍 [DATA INTEGRITY] 무결성 검증 실행');
    return safeApiCall<import('../types/admin').DataIntegrityReport>('post', '/data-integrity/check');
  },

  getIntegrityReport: async (): Promise<import('../types/admin').DataIntegrityReport> => {
    console.log('🔍 [DATA INTEGRITY] 무결성 리포트 조회');
    return safeApiCall<import('../types/admin').DataIntegrityReport>('get', '/data-integrity/report');
  },

  fixIssue: async (issueId: string): Promise<{ success: boolean; message: string }> => {
    console.log(`🔧 [DATA INTEGRITY] 이슈 수정: ${issueId}`);
    return safeApiCall<{ success: boolean; message: string }>('post', `/data-integrity/fix/${issueId}`);
  }
};

// 시스템 설정
export const adminSystemSettings = {
  getSettings: async (): Promise<SystemSettingsResponse> => {
    console.log('⚙️ [SYSTEM SETTINGS] 시스템 설정 조회');
    return safeApiCall<SystemSettingsResponse>('get', '/settings');
  },

  updateSettings: async (settings: Partial<SystemSettings>): Promise<{ success: boolean; message: string; settings: SystemSettings }> => {
    console.log('⚙️ [SYSTEM SETTINGS] 시스템 설정 업데이트:', settings);
    return safeApiCall<{ success: boolean; message: string; settings: SystemSettings }>('put', '/settings', settings);
  }
};

// 브릿지 진단 결과 관리
export const adminBridgeDiagnostics = {
  getDiagnosticReports: async (params?: {
    page?: number;
    pageSize?: number;
    storeId?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ items: DiagnosticReport[]; total: number; page: number; pageSize: number }> => {
    console.log('🔍 [BRIDGE DIAGNOSTICS] 진단 결과 목록 조회:', params);
    let url = '/diagnostic-reports';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key as keyof typeof params] !== undefined && params[key as keyof typeof params] !== null) {
          queryParams.append(key, params[key as keyof typeof params]!.toString());
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    const response = await safeApiCall<{ items: DiagnosticReport[]; total: number; page: number; pageSize: number }>('get', url);
    return response;
  },

  getDiagnosticReport: async (id: number): Promise<DiagnosticReport> => {
    console.log('🔍 [BRIDGE DIAGNOSTICS] 진단 결과 상세 조회:', id);
    return safeApiCall<DiagnosticReport>('get', `/diagnostic-reports/${id}`);
  },

  getDiagnosticSummary: async (): Promise<DiagnosticSummary> => {
    console.log('📊 [BRIDGE DIAGNOSTICS] 진단 결과 통계 조회');
    return safeApiCall<DiagnosticSummary>('get', '/diagnostic-reports/summary');
  }
};

// 브로드캐스트 푸시 알림 관리
export const adminPushBroadcast = {
  sendBroadcast: async (request: SendBroadcastNotificationRequest): Promise<SendBroadcastNotificationResponse> => {
    console.log('📢 [PUSH BROADCAST] 브로드캐스트 알림 발송:', request);
    // API Gateway를 통해 /api/admin/push/broadcast로 요청
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast`;
    const headers = createAuthHeaders();
    const response = await axios.post<SendBroadcastNotificationResponse>(url, request, { headers });
    return response.data;
  },

  getHistory: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<BroadcastNotificationHistoryListResponse> => {
    console.log('📋 [PUSH BROADCAST] 발송 이력 조회:', params);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    let url = `${baseURL}/admin/push/broadcast/history`;
    const queryParams = new URLSearchParams();
    const page = Math.max(1, Math.floor(Number(params?.page) || 1));
    const pageSize = Math.max(1, Math.min(100, Math.floor(Number(params?.pageSize) || 20)));
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());
    if (params) {
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }
    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    const headers = createAuthHeaders();
    const response = await axios.get<BroadcastNotificationHistoryListResponse>(url, { headers });
    return response.data;
  },

  getHistoryById: async (id: number): Promise<BroadcastNotificationHistory> => {
    console.log('📄 [PUSH BROADCAST] 발송 이력 상세 조회:', id);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/history/${id}`;
    const headers = createAuthHeaders();
    const response = await axios.get<BroadcastNotificationHistory>(url, { headers });
    return response.data;
  },

  deleteHistory: async (id: number): Promise<void> => {
    console.log('🗑️ [PUSH BROADCAST] 발송 이력 삭제:', id);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/history/${id}`;
    const headers = createAuthHeaders();
    await axios.delete(url, { headers });
  },

  deleteHistoryBulk: async (ids: number[]): Promise<{ deletedCount: number; message: string }> => {
    console.log('🗑️ [PUSH BROADCAST] 발송 이력 선택 삭제:', ids.length, '건');
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/history/bulk-delete`;
    const headers = createAuthHeaders();
    const response = await axios.post<{ deletedCount: number; message: string }>(url, { ids }, { headers });
    return response.data;
  },

  deleteAllHistory: async (): Promise<{ deletedCount: number; message: string }> => {
    console.log('🗑️ [PUSH BROADCAST] 발송 이력 전체 삭제');
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/history/all`;
    const headers = createAuthHeaders();
    const response = await axios.delete<{ deletedCount: number; message: string }>(url, { headers });
    return response.data;
  },

  getStats: async (): Promise<{
    activeStoreCount: number;
    totalActiveSubscriptions: number;
    totalBroadcastCount: number;
    totalNotificationsSent: number;
  }> => {
    console.log('📊 [PUSH BROADCAST] 통계 조회');
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/stats`;
    const headers = createAuthHeaders();
    const response = await axios.get<{
      activeStoreCount: number;
      totalActiveSubscriptions: number;
      totalBroadcastCount: number;
      totalNotificationsSent: number;
    }>(url, { headers });
    return response.data;
  },

  getTargetStores: async (): Promise<{ storeId: number; subscriptionCount: number }[]> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-stores`;
    const headers = createAuthHeaders();
    const response = await axios.get<{ storeId: number; subscriptionCount: number }[]>(url, { headers });
    return response.data ?? [];
  },

  getTargetSubscriptions: async (): Promise<
    { id: number; userId: string; storeId: number | null; deviceId: string; updatedAt: string }[]
  > => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-subscriptions`;
    const headers = createAuthHeaders();
    const response = await axios.get<
      { id: number; userId: string; storeId: number | null; deviceId: string; updatedAt: string }[]
    >(url, { headers });
    return response.data ?? [];
  },

  deleteTargetStore: async (storeId: number): Promise<{ deactivatedCount: number; message: string }> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-stores/${storeId}`;
    const headers = createAuthHeaders();
    const response = await axios.delete<{ deactivatedCount: number; message: string }>(url, { headers });
    return response.data;
  },

  deleteTargetSubscription: async (id: number): Promise<void> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-subscriptions/${id}`;
    const headers = createAuthHeaders();
    await axios.delete(url, { headers });
  },

  deleteTargetSubscriptionsBulk: async (ids: number[]): Promise<{ deletedCount: number; message: string }> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-subscriptions/bulk-delete`;
    const headers = createAuthHeaders();
    const response = await axios.post<{ deletedCount: number; message: string }>(url, { ids }, { headers });
    return response.data;
  },

  deleteAllTargetSubscriptions: async (): Promise<{ deletedCount: number; message: string }> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-subscriptions/all`;
    const headers = createAuthHeaders();
    const response = await axios.delete<{ deletedCount: number; message: string }>(url, { headers });
    return response.data;
  },

  deleteTargetStoresBulk: async (storeIds: number[]): Promise<{ deletedCount: number; message: string }> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-stores/bulk-delete`;
    const headers = createAuthHeaders();
    const response = await axios.post<{ deletedCount: number; message: string }>(url, { storeIds }, { headers });
    return response.data;
  },

  deleteAllTargetStores: async (): Promise<{ deletedCount: number; message: string }> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/broadcast/target-stores/all`;
    const headers = createAuthHeaders();
    const response = await axios.delete<{ deletedCount: number; message: string }>(url, { headers });
    return response.data;
  },
};

// 정기 브로드캐스트 푸시 알림 스케줄 관리
export const adminScheduledPush = {
  list: async (): Promise<ScheduledBroadcastNotificationListResponse> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/scheduled`;
    const headers = createAuthHeaders();
    const response = await axios.get<ScheduledBroadcastNotificationListResponse>(url, { headers });
    return response.data;
  },

  getById: async (id: number): Promise<ScheduledBroadcastNotification> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/scheduled/${id}`;
    const headers = createAuthHeaders();
    const response = await axios.get<ScheduledBroadcastNotification>(url, { headers });
    return response.data;
  },

  create: async (
    request: CreateScheduledBroadcastNotificationRequest
  ): Promise<ScheduledBroadcastNotification> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/scheduled`;
    const headers = createAuthHeaders();
    const response = await axios.post<ScheduledBroadcastNotification>(url, request, { headers });
    return response.data;
  },

  update: async (
    id: number,
    request: UpdateScheduledBroadcastNotificationRequest
  ): Promise<ScheduledBroadcastNotification> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/scheduled/${id}`;
    const headers = createAuthHeaders();
    const response = await axios.put<ScheduledBroadcastNotification>(url, request, { headers });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/push/scheduled/${id}`;
    const headers = createAuthHeaders();
    await axios.delete(url, { headers });
  },
};

// 분석 및 리포트

// 프로모션 코드 관리 (Gateway → Identity /api/admin/promotions)
export const adminPromotions = {
  getList: async (purpose?: string, isActive?: boolean): Promise<PromotionCodeAdmin[]> => {
    const params: Record<string, string> = {};
    if (purpose != null && purpose !== '') params.purpose = purpose;
    if (isActive !== undefined) params.isActive = String(isActive);
    const headers = createAuthHeaders();
    const response = await adminApi.get<PromotionCodeAdmin[]>('/promotions', { params, headers });
    return response.data ?? [];
  },

  getById: async (id: number): Promise<PromotionCodeAdmin> => {
    const headers = createAuthHeaders();
    const response = await adminApi.get<PromotionCodeAdmin>(`/promotions/${id}`, { headers });
    return response.data;
  },

  getStats: async (id: number): Promise<{ promotionCodeId: number; useCount: number }> => {
    const headers = createAuthHeaders();
    const response = await adminApi.get<{ promotionCodeId: number; useCount: number }>(`/promotions/${id}/stats`, { headers });
    return response.data;
  },

  create: async (body: CreatePromotionRequest): Promise<PromotionCodeAdmin> => {
    const headers = createAuthHeaders();
    const response = await adminApi.post<PromotionCodeAdmin>('/promotions', body, { headers });
    return response.data;
  },

  update: async (id: number, body: UpdatePromotionRequest): Promise<PromotionCodeAdmin> => {
    const headers = createAuthHeaders();
    const response = await adminApi.put<PromotionCodeAdmin>(`/promotions/${id}`, body, { headers });
    return response.data;
  },

  deactivate: async (id: number): Promise<void> => {
    const headers = createAuthHeaders();
    await adminApi.patch(`/promotions/${id}/deactivate`, {}, { headers });
  },

  exportCsv: async (): Promise<Blob> => {
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/admin/promotions/export?format=csv`;
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const headers: Record<string, string> = { Accept: 'text/csv' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('내보내기 실패');
    return response.blob();
  },
};

// 파트너사 관리 (Gateway → Identity /api/admin/partners)
export const adminPartners = {
  getList: async (isActive?: boolean, year?: number, month?: number): Promise<PartnerListDto[]> => {
    const params: Record<string, string | number> = {};
    if (isActive !== undefined) params.isActive = String(isActive);
    if (year !== undefined) params.year = year;
    if (month !== undefined) params.month = month;
    const headers = createAuthHeaders();
    const response = await adminApi.get<PartnerListDto[]>('/partners', { params, headers });
    return response.data ?? [];
  },
  getById: async (id: number): Promise<PartnerDetailDto> => {
    const headers = createAuthHeaders();
    const response = await adminApi.get<PartnerDetailDto>(`/partners/${id}`, { headers });
    return response.data;
  },
  getSignupLink: async (id: number): Promise<{ signupLink: string; partnerCode: string }> => {
    const headers = createAuthHeaders();
    const response = await adminApi.get<{ signupLink: string; partnerCode: string }>(`/partners/${id}/signup-link`, { headers });
    return response.data;
  },
  getSettlements: async (id: number, year: number, month: number): Promise<PartnerSettlementsDto> => {
    const headers = createAuthHeaders();
    const response = await adminApi.get<PartnerSettlementsDto>(`/partners/${id}/settlements`, { params: { year, month }, headers });
    return response.data;
  },
  create: async (body: CreatePartnerRequest): Promise<Partner> => {
    const headers = createAuthHeaders();
    const response = await adminApi.post<Partner>('/partners', body, { headers });
    return response.data;
  },
  update: async (id: number, body: UpdatePartnerRequest): Promise<void> => {
    const headers = createAuthHeaders();
    await adminApi.put(`/partners/${id}`, body, { headers });
  },
  sendLinkEmail: async (id: number): Promise<void> => {
    const headers = createAuthHeaders();
    await adminApi.post(`/partners/${id}/send-link-email`, {}, { headers });
  },
  sendSettlementEmail: async (id: number, year: number, month: number): Promise<void> => {
    const headers = createAuthHeaders();
    await adminApi.post(`/partners/${id}/send-settlement-email`, {}, { params: { year, month }, headers });
  },
  sendMonthlySummaries: async (year: number, month: number): Promise<{ sent: number; total: number }> => {
    const headers = createAuthHeaders();
    const res = await adminApi.post<{ sent: number; total: number }>('/partners/send-monthly-summaries', {}, { params: { year, month }, headers });
    return res.data;
  },
  generateStatsToken: async (id: number): Promise<{ token: string; statsUrl: string }> => {
    const headers = createAuthHeaders();
    const res = await adminApi.post<{ token: string; statsUrl: string }>(`/partners/${id}/generate-stats-token`, {}, { headers });
    return res.data;
  },
};

// 배너 관리
export const adminBanner = {
  getAllBanners: async (): Promise<Banner[]> => {
    console.log('🖼️ [BANNER] 배너 목록 조회');
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin`;
    const headers = createAuthHeaders();
    const response = await axios.get<Banner[]>(url, { headers });
    return response.data;
  },

  getBannerById: async (id: number): Promise<Banner> => {
    console.log('🖼️ [BANNER] 배너 조회:', id);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}`;
    const headers = createAuthHeaders();
    const response = await axios.get<Banner>(url, { headers });
    return response.data;
  },

  createBanner: async (request: CreateBannerRequest): Promise<Banner> => {
    console.log('🖼️ [BANNER] 배너 생성:', request);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin`;
    const headers = createAuthHeaders();
    const response = await axios.post<Banner>(url, request, { headers });
    return response.data;
  },

  updateBanner: async (id: number, request: UpdateBannerRequest): Promise<Banner> => {
    console.log('🖼️ [BANNER] 배너 수정:', id, request);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}`;
    const headers = createAuthHeaders();
    const response = await axios.put<Banner>(url, request, { headers });
    return response.data;
  },

  deleteBanner: async (id: number): Promise<void> => {
    console.log('🖼️ [BANNER] 배너 삭제:', id);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}`;
    const headers = createAuthHeaders();
    await axios.delete(url, { headers });
  },

  toggleBanner: async (id: number): Promise<Banner> => {
    console.log('🖼️ [BANNER] 배너 토글:', id);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}/toggle`;
    const headers = createAuthHeaders();
    const response = await axios.put<Banner>(url, {}, { headers });
    return response.data;
  },

  copyBanner: async (id: number): Promise<Banner> => {
    console.log('🖼️ [BANNER] 배너 복사:', id);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}/copy`;
    const headers = createAuthHeaders();
    const response = await axios.post<Banner>(url, {}, { headers });
    return response.data;
  },

  uploadBannerImage: async (
    bannerId: number,
    type: 'desktop' | 'mobile',
    file: File,
    onProgress?: (progress: number) => void,
    abortSignal?: AbortSignal
  ): Promise<ImageUploadResponse> => {
    console.log('🖼️ [BANNER] 이미지 업로드:', bannerId, type, file.name);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/upload`;
    
    const formData = new FormData();
    formData.append('bannerId', bannerId.toString());
    formData.append('type', type);
    formData.append('file', file);

    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const headers: any = {
      'Content-Type': 'multipart/form-data',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      headers,
      signal: abortSignal, // AbortSignal 추가
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    };

    const response = await axios.post<ImageUploadResponse>(url, formData, config);
    return response.data;
  },

  updatePriority: async (id: number, priority: number): Promise<Banner> => {
    console.log('🖼️ [BANNER] 우선순위 업데이트:', id, priority);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}/priority`;
    const headers = createAuthHeaders();
    const response = await axios.put<Banner>(url, { priority }, { headers });
    return response.data;
  },

  batchDeleteBanners: async (ids: number[]): Promise<{ message: string; deletedCount: number; failedIds: number[] }> => {
    console.log('🖼️ [BANNER] 일괄 삭제:', ids);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/batch`;
    const headers = createAuthHeaders();
    const response = await axios.delete<{ message: string; deletedCount: number; failedIds: number[] }>(url, {
      headers,
      data: { ids }
    });
    return response.data;
  },

  batchToggleBanners: async (ids: number[]): Promise<{ message: string; updatedCount: number; failedIds: number[] }> => {
    console.log('🖼️ [BANNER] 일괄 토글:', ids);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/batch/toggle`;
    const headers = createAuthHeaders();
    const response = await axios.put<{ message: string; updatedCount: number; failedIds: number[] }>(url, { ids }, { headers });
    return response.data;
  },

  getBannerStats: async (id: number): Promise<BannerStats> => {
    console.log('🖼️ [BANNER] 배너 통계 조회:', id);
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}/stats`;
    const headers = createAuthHeaders();
    const response = await axios.get<BannerStats>(url, { headers });
    return response.data;
  },

  getBannerClickLogs: async (
    id: number,
    page: number = 1,
    pageSize: number = 20,
    sortBy: string = 'ClickedAt',
    sortDescending: boolean = true
  ): Promise<PagedResponse<BannerClickLog>> => {
    console.log('🖼️ [BANNER] 배너 클릭 로그 조회:', id, { page, pageSize, sortBy, sortDescending });
    const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
    const url = `${baseURL}/banner/admin/${id}/click-logs`;
    const headers = createAuthHeaders();
    const response = await axios.get<PagedResponse<BannerClickLog>>(url, {
      headers,
      params: { page, pageSize, sortBy, sortDescending }
    });
    return response.data;
  }
};

// 일반 사용자용 배너 API (인증 없이 호출)
export const bannerApi = {
  getActiveBanner: async (): Promise<Banner | null> => {
    try {
      console.log('🖼️ [BANNER] 활성 배너 조회');
      const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
      const url = `${baseURL}/banner/active`;
      const response = await axios.get<Banner | null>(url, {
        timeout: 10000, // 10초 타임아웃
      });
      // 백엔드에서 활성 배너가 없을 때는 200 OK와 null을 반환
      return response.data ?? null;
    } catch (error: any) {
      // 네트워크 오류 등 예외 상황만 처리
      console.warn('⚠️ [BANNER] 활성 배너 조회 실패:', error);
      // 에러 발생해도 앱 진입은 정상 진행
      return null;
    }
  },

  recordClick: async (id: number, request?: BannerClickRequest): Promise<void> => {
    try {
      console.log('🖼️ [BANNER] 클릭 통계 수집:', id, request);
      const baseURL = adminApi.defaults.baseURL?.replace('/admin', '') || '';
      const url = `${baseURL}/banner/admin/${id}/click`;
      await axios.post(url, request || {}, { timeout: 5000 });
    } catch (error) {
      // 통계 수집 실패는 무시
      console.warn('⚠️ [BANNER] 클릭 통계 수집 실패:', error);
    }
  }
};

/** 분석·리포트 보조 API (관리자) */
export const adminAnalyticsExtras = {
  getDailyReportCostBasis: async (params?: {
    storeId?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.storeId != null && params.storeId > 0) q.set('storeId', String(params.storeId));
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    if (params?.page != null && params.page >= 1) q.set('page', String(params.page));
    if (params?.pageSize != null && params.pageSize >= 1) q.set('pageSize', String(params.pageSize));
    const qs = q.toString();
    return safeApiCall<{
      items: Array<{
        id: number;
        storeId: number;
        reportDate: string;
        businessDate: string;
        costBasis: string;
        fifoTotal: number | null;
        purchaseAnalyticsSum: number;
        reason: string | null;
        createdAt: string;
      }>;
      total: number;
      page: number;
      pageSize: number;
    }>('get', `/analytics/daily-report-cost-basis${qs ? `?${qs}` : ''}`);
  },
};

export { adminApi };